import {
  addMonths,
  differenceInCalendarMonths,
  endOfMonth,
  startOfMonth,
} from 'date-fns';
import { useMemo } from 'react';

import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import DateAxisTick from './components/DateAxisTick';
import SampleDot from './components/SampleDot';
import ValueAxisTick from './components/ValueAxisTick';
import useGetTaxaOverTime from './hooks/useGetTaxaOverTime';
import styles from './TaxaOverTimeLineChart.module.css';

const X_AXIS_MAX_TICKS = 5;

type TaxaOverTimeLineChartProps = {
  taxa: number[];
  range: number;
  dataType: string;
  animate?: boolean;
};
export const TaxaOverTimeLineChart = ({
  taxa,
  range,
  dataType,
  animate = true,
}: TaxaOverTimeLineChartProps) => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const taxaOverTimeData = useGetTaxaOverTime({ metricId: taxa[0] });

  const chartData = useMemo(
    () =>
      taxaOverTimeData.map(point => ({
        ...point,
        sample_ts: new Date(point.sample_date).getTime(),
      })),
    [taxaOverTimeData],
  );

  const currentKitBarIndex: number | null = useMemo(() => {
    let tempIndex = null;
    chartData.forEach((data, idx) => {
      if (data.kit_id === currentKitId) {
        tempIndex = idx;
      }
    });
    return tempIndex;
  }, [chartData, currentKitId]);

  const { xDomain, xTicks } = useMemo(() => {
    if (!chartData.length)
      return { xDomain: [0, 1] as [number, number], xTicks: [] as number[] };
    const firstDate = new Date(chartData[0].sample_date);
    const lastDate = new Date(chartData[chartData.length - 1].sample_date);
    const start = startOfMonth(firstDate);
    const end =
      differenceInCalendarMonths(lastDate, firstDate) < 1
        ? addMonths(start, 1)
        : endOfMonth(lastDate);
    const monthsSpan = differenceInCalendarMonths(end, start);
    const tickCount = Math.max(
      2,
      Math.min(chartData.length, monthsSpan + 1, X_AXIS_MAX_TICKS),
    );
    const startTs = start.getTime();
    const endTs = end.getTime();
    return {
      xDomain: [startTs, endTs] as [number, number],
      xTicks: Array.from(
        { length: tickCount },
        (_, i) => startTs + ((endTs - startTs) * i) / (tickCount - 1),
      ),
    };
  }, [chartData]);

  return chartData.length ? (
    <div className={styles.overtimeWrapper}>
      <ResponsiveContainer width='99%' height={200}>
        <AreaChart
          id={taxa.join('')}
          data={chartData}
          margin={{
            left: 15,
            right: 0,
            top: 10,
            bottom: 10,
          }}
        >
          <CartesianGrid
            stroke={'var(--warm-grey-300)'}
            strokeDasharray='3 3'
          />
          <XAxis
            dataKey={'sample_ts'}
            type='number'
            domain={xDomain}
            ticks={xTicks}
            interval={0}
            tick={<DateAxisTick />}
            axisLine={{ stroke: 'var(--warm-grey-400)' }}
            tickLine={{ stroke: 'var(--warm-grey-400)' }}
          />
          <YAxis
            orientation='right'
            domain={[0, range / 10000]}
            padding={{ top: 30 }}
            tick={<ValueAxisTick dataType={dataType} />}
            tickMargin={50}
            axisLine={{ stroke: 'var(--warm-grey-400)' }}
          />
          <Area
            type='linear'
            dataKey='user_value'
            isAnimationActive={animate}
            strokeWidth={2}
            stroke={'var(--warm-grey-400)'}
            fill='transparent'
            activeDot={
              <SampleDot
                sampleIndex={currentKitBarIndex}
                isActive={true}
                dataType={dataType}
              />
            }
            dot={
              <SampleDot
                sampleIndex={currentKitBarIndex}
                isActive={false}
                dataType={dataType}
              />
            }
          />
          <Tooltip content={() => null} cursor={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  ) : null;
};
